import { useCallback, useEffect, useState } from "react";
import Spinner from "../../components/ui/Spinner";
import { EmptyState, ErrorState, Help, HowItWorks, ModalShell, PersonCell, SectionCard, btnGhost, btnPrimary, errMsg, inputCls, labelCls } from "../../components/admin/promotionsUi";
import { useAdminToast } from "../../context/AdminToastContext";
import { formatISTDateTime } from "../../utils/dateUtils";
import { TermsEditor } from "../../components/admin/TermsEditor";
import { promotionsService } from "../../features/promotions/service";
import type { Coupon, CouponAudience, CouponPayload, CouponRedemption, CouponType } from "../../features/promotions/types";
import { COUPON_STATUS_BADGE, CODE_RE, MAX_CREDITS, REDEMPTIONS_PAGE, isoToIstInput, istInputToIso, parseIntStrict } from "./promotionsPageUtils";

// ── Coupons panel ────────────────────────────────────────────────────────────
interface CouponDraft {
  code: string;
  title: string;
  description: string;
  terms: string;
  listed: boolean;
  audience: CouponAudience;
  type: CouponType;
  credits: string;
  percent: string;
  max_bonus_credits: string;
  min_topup_inr: string;
  starts_at: string;
  expires_at: string;
  total_cap: string;
  per_user_cap: string;
  active: boolean;
}

function numStr(v: number | null | undefined): string {
  return v === null || v === undefined ? "" : String(v);
}

/** Lenient body for the terms preview only (the real save validates strictly). */
function previewBody(d: CouponDraft): CouponPayload {
  const int = (v: string) => (v.trim() && Number.isFinite(Number(v)) ? Math.trunc(Number(v)) : null);
  const iso = (v: string) => {
    try {
      return v ? istInputToIso(v) : null;
    } catch {
      return null;
    }
  };
  return {
    code: d.code.trim().toUpperCase() || "CODE",
    title: null,
    description: null,
    terms: d.terms.trim() || null,
    listed: d.listed,
    audience: d.audience,
    type: d.type,
    credits: d.type === "credits" ? int(d.credits) : null,
    percent: d.type === "topup_bonus" && d.percent.trim() ? Number(d.percent) : null,
    max_bonus_credits: d.type === "topup_bonus" ? int(d.max_bonus_credits) : null,
    min_topup_inr: d.type === "topup_bonus" ? int(d.min_topup_inr) : null,
    starts_at: iso(d.starts_at),
    expires_at: iso(d.expires_at) as string,
    total_cap: int(d.total_cap),
    per_user_cap: int(d.per_user_cap) ?? 1,
    active: d.active,
  };
}

function CouponModal({ initial, onClose, onSaved, toast }: { initial: Coupon | null; onClose: () => void; onSaved: () => void; toast: ReturnType<typeof useAdminToast> }) {
  const [d, setD] = useState<CouponDraft>(() =>
    initial
      ? {
          code: initial.code,
          title: initial.title ?? "",
          description: initial.description ?? "",
          terms: initial.terms ?? "",
          listed: initial.listed === true,
          audience: initial.audience,
          type: initial.type,
          credits: numStr(initial.credits),
          percent: numStr(initial.percent),
          max_bonus_credits: numStr(initial.max_bonus_credits),
          min_topup_inr: numStr(initial.min_topup_inr),
          starts_at: isoToIstInput(initial.starts_at),
          expires_at: isoToIstInput(initial.expires_at),
          total_cap: numStr(initial.total_cap),
          per_user_cap: String(initial.per_user_cap),
          active: initial.active,
        }
      : { code: "", title: "", description: "", terms: "", listed: false, audience: "both", type: "credits", credits: "", percent: "", max_bonus_credits: "", min_topup_inr: "", starts_at: "", expires_at: "", total_cap: "", per_user_cap: "1", active: true },
  );
  const [saving, setSaving] = useState(false);
  const patch = (p: Partial<CouponDraft>) => setD((prev) => ({ ...prev, ...p }));

  const optInt = (v: string, label: string): number | null | undefined => {
    if (!v.trim()) return null;
    const n = parseIntStrict(v);
    if (n === null || n < 1) {
      toast.showError(`${label} must be a whole number of at least 1 (or blank)`);
      return undefined;
    }
    return n;
  };

  const submit = async () => {
    const code = d.code.trim().toUpperCase();
    if (!CODE_RE.test(code)) return toast.showError("Code must be 3-32 characters: A-Z, 0-9, _ or -");
    let credits: number | null = null;
    let percent: number | null = null;
    if (d.type === "credits") {
      const n = parseIntStrict(d.credits);
      if (n === null || n < 1 || n > MAX_CREDITS) return toast.showError(`Credits must be a whole number 1-${MAX_CREDITS}`);
      credits = n;
    } else {
      const p = Number(d.percent);
      if (!d.percent.trim() || !Number.isFinite(p) || p <= 0 || p > 100) return toast.showError("Percent must be greater than 0 and at most 100");
      percent = p;
    }
    const maxBonus = d.type === "topup_bonus" ? optInt(d.max_bonus_credits, "Max bonus credits") : null;
    if (maxBonus === undefined) return;
    const minTopup = d.type === "topup_bonus" ? optInt(d.min_topup_inr, "Min top-up (INR)") : null;
    if (minTopup === undefined) return;
    const totalCap = optInt(d.total_cap, "Total cap");
    if (totalCap === undefined) return;
    const perUser = parseIntStrict(d.per_user_cap);
    if (perUser === null || perUser < 1) return toast.showError("Per-user cap must be a whole number of at least 1");
    if (!d.expires_at) return toast.showError("Expiry (IST) is required");
    const expiresIso = istInputToIso(d.expires_at);
    const startsIso = d.starts_at ? istInputToIso(d.starts_at) : null;
    if (startsIso && new Date(expiresIso).getTime() <= new Date(startsIso).getTime()) return toast.showError("Expiry must be after start");
    const body: CouponPayload = {
      code,
      title: d.title.trim() || null,
      description: d.description.trim() || null,
      terms: d.terms.trim() || null,
      listed: d.listed,
      audience: d.audience,
      type: d.type,
      credits,
      percent,
      max_bonus_credits: maxBonus,
      min_topup_inr: minTopup,
      starts_at: startsIso,
      expires_at: expiresIso,
      total_cap: totalCap,
      per_user_cap: perUser,
      active: d.active,
    };
    setSaving(true);
    try {
      if (initial) await promotionsService.updateCoupon(initial.id, body);
      else await promotionsService.createCoupon(body);
      toast.showSuccess(initial ? "Coupon updated" : "Coupon created");
      onSaved();
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      toast.showError(status === 409 ? "A coupon with this code already exists" : errMsg(e, "Failed to save coupon"));
    } finally {
      setSaving(false);
    }
  };

  const pctNum = Number(d.percent);
  const maxNum = parseIntStrict(d.max_bonus_credits);
  const exampleBonus = d.percent.trim() && Number.isFinite(pctNum) && pctNum > 0 && pctNum <= 100
    ? Math.round(((pctNum * 1000) / 100) * 100) / 100
    : null;
  const exampleFinal = exampleBonus === null ? null : maxNum && maxNum > 0 ? Math.min(exampleBonus, maxNum) : exampleBonus;

  return (
    <ModalShell title={initial ? `Edit coupon ${initial.code}` : "New coupon"} onClose={onClose} wide>
      <div className="space-y-4">
        <HowItWorks>
          <p>Credits type: the user types the code in Wallet, Redeem coupon, and flat credits are added at once.</p>
          <p>Top-up bonus type: the user enters the code at top-up checkout; extra % credits are added only when the payment succeeds. Stripe top-ups cannot use coupons.</p>
          <p>Wrong guesses all show the same generic message, so users cannot probe which codes exist.</p>
        </HowItWorks>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Code</label>
            <input className={`${inputCls} font-mono${initial ? " opacity-60 cursor-not-allowed" : ""}`} value={d.code} onChange={(e) => patch({ code: e.target.value.toUpperCase() })} placeholder="DIWALI500" maxLength={32} readOnly={!!initial} />
            <Help>What users type. A-Z, 0-9, _ and - only (3-32 chars). Saved in capitals. Cannot be changed after creation.</Help>
          </div>
          <div>
            <label className={labelCls}>Audience</label>
            <select className={inputCls} value={d.audience} onChange={(e) => patch({ audience: e.target.value as CouponAudience })}>
              <option value="both">Sellers and buyers</option>
              <option value="seller">Sellers only</option>
              <option value="buyer">Buyers only</option>
            </select>
            <Help>Who may use it. Anyone else gets a generic invalid-code message.</Help>
          </div>
        </div>
        <div>
          <label className={labelCls}>Description (optional)</label>
          <input className={inputCls} value={d.description} onChange={(e) => patch({ description: e.target.value })} maxLength={200} placeholder="Diwali 2026 launch offer" />
          <Help>Optional note on what this code is for. Users see it only if the coupon is shown in the available coupons list.</Help>
        </div>
        <TermsEditor
          value={d.terms}
          onChange={(v) => patch({ terms: v })}
          draft={previewBody(d)}
          loadPreview={() => promotionsService.previewCouponTerms(previewBody(d))}
          textareaCls={inputCls}
          labelCls={labelCls}
        />
        <div>
          <label className={labelCls}>Card title (optional)</label>
          <input className={inputCls} value={d.title} onChange={(e) => patch({ title: e.target.value })} maxLength={80} placeholder="10% extra credits on top-ups" />
          <Help>Heading on the user's coupon card. Blank = the benefit text (for example "10% bonus credits").</Help>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Type</label>
            <select className={inputCls} value={d.type} onChange={(e) => patch({ type: e.target.value as CouponType })}>
              <option value="credits">Flat credits</option>
              <option value="topup_bonus">Top-up bonus (%)</option>
            </select>
            <Help>
              {d.type === "credits"
                ? "Credits: flat credits added when the user redeems it in Wallet."
                : "Top-up bonus: extra % credits added when the user's top-up payment succeeds."}
            </Help>
          </div>
          {d.type === "credits" ? (
            <div>
              <label className={labelCls}>Credits</label>
              <input type="number" min={1} max={MAX_CREDITS} className={inputCls} value={d.credits} onChange={(e) => patch({ credits: e.target.value })} placeholder="100" />
              <Help>Flat credits added to the wallet on redeeming. Whole number, 1 or more. Example: 100 credits per redemption.</Help>
            </div>
          ) : (
            <div>
              <label className={labelCls}>Bonus percent (0-100)</label>
              <input type="number" min={0} max={100} step="any" className={inputCls} value={d.percent} onChange={(e) => patch({ percent: e.target.value })} placeholder="10" />
              <Help>Extra credits as % of credits bought. 10 on a 1000-credit top-up gives 100.</Help>
            </div>
          )}
        </div>
        {d.type === "topup_bonus" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Max bonus credits (blank = no limit)</label>
              <input type="number" min={1} className={inputCls} value={d.max_bonus_credits} onChange={(e) => patch({ max_bonus_credits: e.target.value })} placeholder="200" />
              <Help>Upper limit on the bonus per top-up. Leave blank for no limit.</Help>
            </div>
            <div>
              <label className={labelCls}>Min top-up in INR (blank = none)</label>
              <input type="number" min={1} className={inputCls} value={d.min_topup_inr} onChange={(e) => patch({ min_topup_inr: e.target.value })} placeholder="500" />
              <Help>Coupon is rejected below this top-up amount. Leave blank for none.</Help>
            </div>
          </div>
        )}
        {d.type === "topup_bonus" && exampleFinal !== null && (
          <p className="text-xs text-indigo-300 bg-indigo-500/10 border border-indigo-500/20 rounded-lg px-3 py-2">
            Example: user tops up 1000 credits {"->"} bonus = min({pctNum}% x 1000{maxNum ? `, ${maxNum}` : ", no max"}) = {exampleFinal} credits
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Starts (IST, optional)</label>
            <input type="datetime-local" className={inputCls} value={d.starts_at} onChange={(e) => patch({ starts_at: e.target.value })} />
            <Help>Optional. Not usable before this time (IST).</Help>
          </div>
          <div>
            <label className={labelCls}>Expires (IST)</label>
            <input type="datetime-local" className={inputCls} value={d.expires_at} onChange={(e) => patch({ expires_at: e.target.value })} />
            <Help>Required. Not usable after this time (IST). A top-up that started before this still gets the bonus.</Help>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Total redemption cap (blank = unlimited)</label>
            <input type="number" min={1} className={inputCls} value={d.total_cap} onChange={(e) => patch({ total_cap: e.target.value })} placeholder="500" />
            <Help>Max total redemptions across all users. Leave blank for unlimited. The coupon shows Exhausted when reached.</Help>
          </div>
          <div>
            <label className={labelCls}>Per-user cap</label>
            <input type="number" min={1} className={inputCls} value={d.per_user_cap} onChange={(e) => patch({ per_user_cap: e.target.value })} placeholder="1" />
            <Help>How many times one user can use it. Usually 1.</Help>
          </div>
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={d.active} onChange={(e) => patch({ active: e.target.checked })} /> Active
          </label>
          <Help>Untick to pause instantly without deleting.</Help>
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={d.listed} onChange={(e) => patch({ listed: e.target.checked })} /> Show in the available coupons list
          </label>
          <Help>Lets eligible users tap Apply on this coupon instead of typing the code (top-up dialog for top-up bonus codes, Wallet page for free credits). Off = the code stays private and works only when typed. Only users who can use it right now see it.</Help>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className={btnGhost}>Cancel</button>
          <button onClick={submit} disabled={saving} className={btnPrimary}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </ModalShell>
  );
}

function RedemptionsDrawer({ coupon, onClose }: { coupon: Coupon; onClose: () => void }) {
  const [rows, setRows] = useState<CouponRedemption[]>([]);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPage = useCallback(async (skip: number) => {
    setLoading(true);
    setError(null);
    try {
      const page = await promotionsService.listRedemptions(coupon.id, REDEMPTIONS_PAGE, skip);
      setRows((prev) => (skip === 0 ? page : [...prev, ...page]));
      setMore(page.length === REDEMPTIONS_PAGE);
    } catch (e) {
      setError(errMsg(e, "Failed to load redemptions"));
      setMore(false);
    } finally {
      setLoading(false);
    }
  }, [coupon.id]);

  useEffect(() => { void loadPage(0); }, [loadPage]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-gray-900 border-l border-gray-800 w-full max-w-md h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-white font-semibold text-lg font-mono">{coupon.code}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-xl leading-none" aria-label="Close">×</button>
        </div>
        <p className="text-gray-500 text-xs mb-4">Redemptions ({coupon.redeemed_count} total)</p>
        {error && <ErrorState message={error} onRetry={() => void loadPage(rows.length)} />}
        {rows.length === 0 && !loading && !error ? (
          <EmptyState title="No redemptions yet" hint="Redemptions appear here with the user's name and email as soon as the code is used." />
        ) : (
          <ul className="space-y-2">
            {rows.map((r) => (
              <li key={r.id} className="bg-gray-800/60 border border-gray-800 rounded-lg px-3 py-2 text-sm">
                <div className="flex justify-between gap-2">
                  <PersonCell person={{ name: r.user_name, email: r.user_email }} fallback="Deleted user" />
                  <span className="text-emerald-400 font-semibold whitespace-nowrap">+{r.credits}</span>
                </div>
                <div className="text-xs text-gray-500 flex justify-between gap-2">
                  <span>{r.kind === "topup_bonus" ? "Top-up bonus" : "Flat credits"}{r.order_id ? ` · ${r.order_id}` : ""}</span>
                  <span className="whitespace-nowrap">{formatISTDateTime(r.created_at)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
        {loading && <div className="flex justify-center py-6"><Spinner /></div>}
        {!loading && more && (
          <button onClick={() => loadPage(rows.length)} className={`${btnGhost} mt-3 w-full border border-gray-800`}>Load more</button>
        )}
      </div>
    </div>
  );
}

export default function CouponsPanel({ toast, reloadKey, onSummary }: { toast: ReturnType<typeof useAdminToast>; reloadKey: number; onSummary: (s: string) => void }) {
  const [rows, setRows] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [modal, setModal] = useState<{ open: boolean; item: Coupon | null }>({ open: false, item: null });
  const [drawer, setDrawer] = useState<Coupon | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const list = await promotionsService.listCoupons();
      setRows(list);
      setLoadError(null);
      const live = list.filter((c) => (c.status ?? (c.active ? "live" : "inactive")) === "live").length;
      onSummary(`${list.length} total, ${live} live`);
    } catch (e) {
      setLoadError(errMsg(e, "Failed to load coupons"));
    } finally {
      setLoading(false);
    }
  }, [onSummary]);

  useEffect(() => { void load(); }, [load, reloadKey]);

  const toggle = async (c: Coupon) => {
    setBusyId(c.id);
    try {
      await promotionsService.setCouponActive(c.id, !c.active);
      toast.showSuccess(c.active ? "Coupon deactivated" : "Coupon activated");
      await load();
    } catch (e) {
      toast.showError(errMsg(e, "Failed to update coupon"));
    } finally {
      setBusyId(null);
    }
  };

  const value = (c: Coupon) => (c.type === "credits" ? `${c.credits ?? 0} credits` : `${c.percent ?? 0}% top-up bonus${c.max_bonus_credits ? ` (max ${c.max_bonus_credits})` : ""}`);

  return (
    <SectionCard
      id="coupons"
      title="Coupons"
      description="Flat-credit codes users redeem in their wallet, and top-up bonus codes applied at checkout. Times are IST."
      action={<button onClick={() => setModal({ open: true, item: null })} className={`${btnPrimary} shrink-0`}>New coupon</button>}
    >
      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : loadError ? (
        <ErrorState message={loadError} onRetry={() => { setLoading(true); void load(); }} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No coupons yet"
          hint="Create a code that gives flat credits when redeemed in the wallet, or a percentage bonus on a top-up."
          action={<button onClick={() => setModal({ open: true, item: null })} className={btnPrimary}>New coupon</button>}
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-800">
                <th className="py-2 pr-3 font-medium">Code</th>
                <th className="py-2 pr-3 font-medium">Benefit</th>
                <th className="py-2 pr-3 font-medium">Audience</th>
                <th className="py-2 pr-3 font-medium">Expires (IST)</th>
                <th className="py-2 pr-3 font-medium">Used</th>
                <th className="py-2 pr-3 font-medium">State</th>
                <th className="py-2 pr-3 font-medium">Last change</th>
                <th className="py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const badge = COUPON_STATUS_BADGE[c.status ?? (c.active ? "live" : "inactive")];
                return (
                  <tr key={c.id} className="border-b border-gray-800/60 text-gray-300">
                    <td className="py-2.5 pr-3 font-mono text-white">
                      {c.code}
                      {c.listed && <span className="ml-2 inline-flex px-2 py-0.5 rounded-full text-xs font-semibold font-sans bg-sky-500/15 text-sky-300">Listed</span>}
                    </td>
                    <td className="py-2.5 pr-3">{value(c)}</td>
                    <td className="py-2.5 pr-3 capitalize">{c.audience}</td>
                    <td className="py-2.5 pr-3 text-xs whitespace-nowrap">{formatISTDateTime(c.expires_at)}</td>
                    <td className="py-2.5 pr-3 whitespace-nowrap">{c.redeemed_count}{c.total_cap ? ` / ${c.total_cap}` : ""}</td>
                    <td className="py-2.5 pr-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${badge.cls}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 text-xs min-w-[8rem]">
                      {(c.updated_by_user ?? c.created_by_user)
                        ? <PersonCell person={c.updated_by_user ?? c.created_by_user} fallback="an admin" />
                        : <span className="text-gray-600">—</span>}
                    </td>
                    <td className="py-2.5 text-right whitespace-nowrap">
                      <button onClick={() => setDrawer(c)} className="text-xs px-2 py-1 rounded-md text-gray-300 hover:bg-gray-800">Redemptions</button>
                      <button onClick={() => setModal({ open: true, item: c })} className="text-xs px-2 py-1 rounded-md text-indigo-400 hover:bg-indigo-500/10">Edit</button>
                      <button onClick={() => toggle(c)} disabled={busyId === c.id} className={`text-xs px-2 py-1 rounded-md disabled:opacity-50 ${c.active ? "text-red-400 hover:bg-red-500/10" : "text-emerald-400 hover:bg-emerald-500/10"}`}>
                        {c.active ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {modal.open && (
        <CouponModal
          initial={modal.item}
          toast={toast}
          onClose={() => setModal({ open: false, item: null })}
          onSaved={() => { setModal({ open: false, item: null }); void load(); }}
        />
      )}
      {drawer && <RedemptionsDrawer coupon={drawer} onClose={() => setDrawer(null)} />}
    </SectionCard>
  );
}

